import { useMemo } from "react";

import { useTranslation } from "@punto/frontend-shared/i18n/i18next";
import { Badge } from "@punto/ui/components/badge";
import { Logo } from "@punto/ui/components/logo";

import { $api } from "@/lib/api/api";
import { useBusinessAbilities } from "@/modules/abilities";
import { useUser } from "@/modules/auth/hooks/use-user";
import { routePaths } from "@/router/route-paths";

const useSignaturesBadgeCount = (enabled: boolean) => {
  const { data } = $api.useQuery(
    "get",
    "/v1/signature-requests/count",
    undefined,
    { enabled },
  );

  return data?.count ?? 0;
};

const useNav = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { canAccessProcedures, canAccessReports, canAccessSignatures } =
    useBusinessAbilities();
  const signaturesBadgeCount = useSignaturesBadgeCount(canAccessSignatures);

  const representationAuthUrl = user?.settings?.digitalRepresentationAuth;

  const homeLink = {
    label: (
      <div className="p-2">
        <Logo />
      </div>
    ),
    href: routePaths.proceduresLanding,
  };

  const menuItems = useMemo(() => {
    const items = [
      ...(canAccessProcedures
        ? [
            {
              label: t("Header.menu_procedures"),
              href: routePaths.proceduresLanding,
            },
          ]
        : []),
      ...(canAccessReports
        ? [
            {
              label: t("Header.menu_reports"),
              href: routePaths.reports,
            },
          ]
        : []),
      ...(canAccessSignatures
        ? [
            {
              label: (
                <span className="inline-flex items-center gap-1.5">
                  {t("Header.menu_signatures")}
                  {signaturesBadgeCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="h-5 min-w-5 justify-center rounded-full px-1.5 text-xs"
                    >
                      <span className="sr-only">
                        {t("Signatures.badge_accessibility", {
                          count: signaturesBadgeCount,
                        })}
                      </span>
                      {signaturesBadgeCount}
                    </Badge>
                  )}
                </span>
              ),
              href: routePaths.signatures,
            },
          ]
        : []),
      ...(representationAuthUrl
        ? [
            {
              label: t("Header.menu_representation_auth"),
              href: representationAuthUrl,
              target: "_blank",
            },
          ]
        : []),
    ];
    return items;
  }, [
    t,
    canAccessProcedures,
    canAccessReports,
    canAccessSignatures,
    signaturesBadgeCount,
    representationAuthUrl,
  ]);

  return { homeLink, menuItems };
};

export { useNav };
